/**
 * Mini Cart Dropdown
 * Shows cart summary after add-to-cart on desktop
 * Green bubble that expands from cart button
 * IMAGE SET: Checks #image-set-map JSON for metaobject images before falling back to item.image
 */

class MiniCartDropdown {
  constructor() {
    this.dropdown = document.getElementById('mini-cart-dropdown');
    this.itemsContainer = document.getElementById('mini-cart-items');
    this.closeBtn = document.getElementById('mini-cart-close');
    this.countEl = document.getElementById('mini-cart-count');
    this.cartButton = document.getElementById('floating-cart-icon');
    this.wrapper = document.querySelector('.mini-cart-wrapper');
    
    this.autoCloseTimer = null;
    this.autoCloseDelay = 5000; // 5 seconds
    this.isDesktop = window.innerWidth >= 990;
    this.isHovered = false;
    this.imageSetMap = {};
    
    if (!this.dropdown) return;
    
    // Load image set map
    this.loadImageSetMap();
    
    // Move dropdown to body to escape any stacking context issues
    document.body.appendChild(this.dropdown);
    
    this.init();
  }

  loadImageSetMap() {
    try {
      const mapEl = document.getElementById('image-set-map');
      if (mapEl) {
        this.imageSetMap = JSON.parse(mapEl.textContent);
      }
    } catch (e) {
      console.warn('Failed to parse image-set-map:', e);
      this.imageSetMap = {};
    }
  }

  getItemImage(item) {
    // Check image set map first
    const imageSetUrl = this.imageSetMap[String(item.variant_id)];
    if (imageSetUrl) return imageSetUrl;
    
    // Fall back to native Shopify variant image
    if (item.image) return this.getSizedImageUrl(item.image, '120x120');
    
    // Placeholder
    return '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
  }

  init() {
    // Only intercept on desktop
    if (this.isDesktop) {
      this.interceptAddToCart();
    }
    
    // Close button
    this.closeBtn?.addEventListener('click', () => this.hide());
    
    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.dropdown.classList.contains('is-visible') && 
          !this.dropdown.contains(e.target) && 
          !e.target.closest('#floating-cart-icon') &&
          !e.target.closest('.mini-cart-wrapper') &&
          !e.target.closest('product-form') &&
          !e.target.closest('[data-product-form]') &&
          !e.target.closest('form[action*="/cart/add"]')) {
        this.hide();
      }
    });

    // Pause auto-close on hover
    this.dropdown.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.clearAutoClose();
    });

    this.dropdown.addEventListener('mouseleave', () => {
      this.isHovered = false;
      this.startAutoClose();
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dropdown.classList.contains('is-visible')) {
        this.hide();
      }
    });

    // Listen for resize to update desktop status
    window.addEventListener('resize', () => {
      this.isDesktop = window.innerWidth >= 990;
      // Reposition if visible
      if (this.dropdown.classList.contains('is-visible')) {
        this.positionDropdown();
      }
    });

    // Reposition on scroll (for sticky header)
    window.addEventListener('scroll', () => {
      if (this.dropdown.classList.contains('is-visible')) {
        this.positionDropdown();
      }
    }, { passive: true });
  }

  interceptAddToCart() {
    // Capture phase to intercept before other handlers
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Check if it's a product add-to-cart form
      const isAddToCartForm = form.action?.includes('/cart/add') || 
                              form.closest('product-form') ||
                              form.querySelector('[name="add"]');
      
      if (!isAddToCartForm) return;
      
      // Only intercept on desktop
      if (!this.isDesktop) return;

      // Stop the form from submitting normally
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Handle add to cart ourselves
      this.addToCart(form);
      
    }, true); // Capture phase

    // Also intercept click on submit buttons directly
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[type="submit"], button[name="add"], .product-form__submit');
      if (!btn) return;
      
      const form = btn.closest('form');
      if (!form) return;
      
      const isAddToCartForm = form.action?.includes('/cart/add') || 
                              form.closest('product-form');
      
      if (!isAddToCartForm) return;
      if (!this.isDesktop) return;

      // Stop the click
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Handle add to cart ourselves
      this.addToCart(form);
      
    }, true); // Capture phase
  }

  async addToCart(form) {
    const submitBtn = form.querySelector('[type="submit"], [name="add"], .product-form__submit');
    const originalBtnText = submitBtn?.innerHTML;
    
    try {
      // Show loading state
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span>Adding...</span>';
      }

      const formData = new FormData(form);
      
      // Add to cart via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Add to cart failed');
      }

      // Fetch updated cart
      const cart = await this.fetchCart();
      
      // Update cart icon bubble count
      this.updateCartBubble(cart.item_count);
      
      // Render and show dropdown
      this.render(cart);
      this.show();

      // Publish cart update event for other components
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart' });
      }

      // Restore button with success state
      if (submitBtn) {
        submitBtn.innerHTML = '<span>Added! ✓</span>';
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
        }, 1500);
      }

    } catch (error) {
      console.error('Mini cart error:', error);
      
      // Show error
      if (submitBtn) {
        submitBtn.innerHTML = `<span>${error.message || 'Error'}</span>`;
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
        }, 2000);
      }
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  updateCartBubble(count) {
    // Update floating header cart count
    const floatingCartCount = document.querySelector('.floating-header__cart-count');
    if (floatingCartCount) {
      floatingCartCount.setAttribute('data-cart-count', count);
      const countSpan = floatingCartCount.querySelector('span[aria-hidden="true"]');
      if (countSpan) {
        countSpan.textContent = count > 0 ? count : '';
      }
      // Show/hide the badge
      floatingCartCount.style.display = count > 0 ? 'flex' : 'none';
    }

    // Also update Dawn's hidden bubble if it exists (for other components)
    const dawnBubble = document.getElementById('cart-icon-bubble');
    if (dawnBubble) {
      dawnBubble.textContent = count;
    }
  }

  render(cart) {
    // Update item count
    if (this.countEl) {
      this.countEl.textContent = `(${cart.item_count})`;
    }
    
    if (cart.items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div class="mini-cart-dropdown__empty">
          Your cart is empty
        </div>
      `;
      return;
    }

    const itemsHtml = cart.items.map(item => `
      <div class="mini-cart-dropdown__item">
        <img 
          src="${this.getItemImage(item)}" 
          alt="${item.title}"
          class="mini-cart-dropdown__item-image"
        >
        <div class="mini-cart-dropdown__item-details">
          <p class="mini-cart-dropdown__item-title">${item.product_title}</p>
          ${item.variant_title ? `<p class="mini-cart-dropdown__item-variant">${item.variant_title}</p>` : ''}
          <div class="mini-cart-dropdown__item-bottom">
            <span class="mini-cart-dropdown__item-qty">Qty: ${item.quantity}</span>
            <span class="mini-cart-dropdown__item-price">${this.formatMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>
    `).join('');

    this.itemsContainer.innerHTML = itemsHtml;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d+)\./, '.').replace(/\.([^.]+)$/, `_${size}.$1`);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  show() {
    // Reset states
    this.dropdown.classList.remove('is-closing');
    this.isHovered = false;
    
    // Position dropdown relative to cart button
    this.positionDropdown();
    
    // Step 1: Flatten cart button corners FIRST
    this.wrapper?.classList.add('is-cart-open');
    
    // Step 2: Show dropdown AFTER corners flatten (150ms)
    setTimeout(() => {
      this.dropdown.classList.add('is-visible');
    }, 150);
    
    // Start auto-close timer
    this.startAutoClose();
  }

  positionDropdown() {
    if (!this.cartButton || !this.dropdown) return;
    
    const rect = this.cartButton.getBoundingClientRect();
    
    // Position dropdown directly below cart button, right edges aligned
    // No gap - they should blend together
    this.dropdown.style.top = `${rect.bottom}px`;
    this.dropdown.style.right = `${window.innerWidth - rect.right}px`;
  }

  hide() {
    this.clearAutoClose();
    
    // Step 1: Add closing class for panel animation
    this.dropdown.classList.add('is-closing');
    
    // Step 2: After panel shrinks, hide it
    setTimeout(() => {
      this.dropdown.classList.remove('is-visible');
      this.dropdown.classList.remove('is-closing');
    }, 250);
    
    // Step 3: Restore cart button corners AFTER modal fully hidden
    setTimeout(() => {
      this.wrapper?.classList.remove('is-cart-open');
    }, 400);
  }

  startAutoClose() {
    this.clearAutoClose();
    
    // Only start timer if not hovered
    if (!this.isHovered) {
      this.autoCloseTimer = setTimeout(() => {
        if (!this.isHovered) {
          this.hide();
        }
      }, this.autoCloseDelay);
    }
  }

  clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.miniCartDropdown = new MiniCartDropdown();
});